// Procurement domain types aligned with the ERP purchase-order REST contracts.
//
// Backend: `App\Modules\PurchaseOrders` — `GET/POST /purchase-orders`,
// `GET/PUT/DELETE /purchase-orders/{id}`, `POST /purchase-orders/{id}/receive`.
// Permissions: `purchase_orders.view | create | update | delete`.

export type PurchaseOrderStatus =
  | 'draft'
  | 'approved'
  | 'ordered'
  | 'partially_received'
  | 'received'
  | 'cancelled'

export const PURCHASE_ORDER_STATUSES: PurchaseOrderStatus[] = [
  'draft',
  'approved',
  'ordered',
  'partially_received',
  'received',
  'cancelled',
]

/** Line returned by `GET /purchase-orders/{id}` → `items[]`. */
export interface PurchaseOrderItem {
  id: number
  purchase_order_id: number
  product_id: number
  product_name: string | null
  sku: string | null
  quantity: number
  price: number
  total: number
  /** Only present on the detail response (`PurchaseOrderService`). */
  received_quantity?: number
  /** Only present on the detail response; never below zero. */
  remaining_quantity?: number
}

/** Row returned by `GET /purchase-orders` and `GET /purchase-orders/{id}`. */
export interface PurchaseOrder {
  id: number
  supplier_agent_id: number
  supplier_name: string | null
  requester_user_id: number
  requester_user_name: string | null
  destination_warehouse_agent_id: number | null
  destination_warehouse_name: string | null
  currency_id: number | null
  currency_code: string | null
  exchange_rate: number
  status: PurchaseOrderStatus | string
  expected_date: string | null
  notes: string | null
  total_amount: number
  created_at: string | null
  /** Only present on the detail response. */
  items?: PurchaseOrderItem[]
  /** Only present on the `POST /purchase-orders/{id}/receive` response. */
  receipt?: ReceiptDocument
}

/** Filters accepted by `GET /purchase-orders`. */
export interface PurchaseOrderFilters {
  search?: string
  status?: string
  supplier_agent_id?: number
  destination_warehouse_agent_id?: number
  requester_user_id?: number
  currency_code?: string
  page?: number
  per_page?: number
}

/** One line in a create/update payload. `total` is computed by the server. */
export interface PurchaseOrderItemPayload {
  product_id: number
  quantity: number
  price: number
}

/** Body for `POST /purchase-orders` and `PUT /purchase-orders/{id}`. */
export interface SavePurchaseOrderPayload {
  supplier_agent_id: number
  requester_user_id: number
  destination_warehouse_agent_id?: number | null
  currency_code: string
  exchange_rate?: number
  status?: PurchaseOrderStatus
  expected_date?: string | null
  notes?: string | null
  items: PurchaseOrderItemPayload[]
}

/** Quality status of an inventory lot (`inventory_lots.quality_status`). */
export type LotQualityStatus = 'pending' | 'approved' | 'rejected' | 'quarantined'

export const LOT_QUALITY_STATUSES: LotQualityStatus[] = ['pending', 'approved', 'rejected', 'quarantined']

/**
 * One receive line for `POST /purchase-orders/{id}/receive`.
 *
 * `purchase_order_item_id` links the line to a specific PO line (the backend also
 * accepts `item_id` / `id`). The lot fields are optional: `lot_number` reuses an
 * existing product lot when found, otherwise a new lot is created with the
 * supplied batch/date metadata. `manufactured_at` is a `Y-m-d H:i:s` timestamp
 * and `expires_at` a `Y-m-d` date.
 */
export interface ReceivePurchaseOrderItem {
  product_id: number
  quantity: number
  purchase_order_item_id?: number
  lot_id?: number
  lot_number?: string
  supplier_batch_number?: string
  production_batch_number?: string
  manufactured_at?: string
  expires_at?: string
  quality_status?: LotQualityStatus
  notes?: string
}

/**
 * Body for `POST /purchase-orders/{id}/receive`.
 *
 * Without `items`, the backend receives every remaining quantity. With
 * `items`, each entry must be a product on the order and no more than the
 * still-unreceived quantity.
 */
export interface ReceivePurchaseOrderPayload {
  destination_warehouse_agent_id?: number
  items?: ReceivePurchaseOrderItem[]
  notes?: string
}

/**
 * Row returned by `GET /purchase-orders/{id}/receipts`.
 *
 * Receiving posts a warehouse receipt document, so movements also carry the
 * source document they were created from.
 */
export interface PurchaseOrderReceipt {
  id: number
  product_id: number
  product_name: string | null
  sku: string | null
  supplier_agent_id: number | null
  supplier_name: string | null
  warehouse_agent_id: number | null
  warehouse_name: string | null
  quantity: number
  movement_type: string
  reference_id: number | null
  source_document_type: string | null
  source_document_id: number | null
  source_document_line_id: number | null
  lot_id: number | null
  lot_number: string | null
  supplier_batch_number: string | null
  production_batch_number: string | null
  quantity_breakdown: unknown
  created_at: string | null
}

/** The warehouse receipt document returned alongside a successful receive. */
export interface ReceiptDocument {
  id: number
  document_number: string | null
  document_type: string
  status: string | null
  warehouse_agent_id: number | null
  from_agent_id: number | null
  reference_id: number | null
  posted_at: string | null
  lines: unknown[]
  movements: unknown[]
}

/** Fields shared by lots, receive lines and receipt movements. */
export interface LotIdentity {
  lot_number?: string | null
  supplier_batch_number?: string | null
  production_batch_number?: string | null
}

/** `LOT-2026-001 · SUP-B-88` — the compact lot/batch summary, empty when unset. */
export function lotLabel(source: LotIdentity): string {
  return [source.lot_number, source.supplier_batch_number, source.production_batch_number]
    .map((value) => (value ?? '').trim())
    .filter((value) => value !== '')
    .join(' · ')
}

/**
 * The PO line id to send as `purchase_order_item_id`, when the product appears
 * exactly once on the order. The backend links the receipt line automatically
 * in that case; repeated products must stay unlinked.
 */
export function uniqueOrderItemId(
  items: readonly Pick<PurchaseOrderItem, 'id' | 'product_id'>[],
  productId: number,
): number | null {
  const matches = items.filter((item) => item.product_id === productId)
  return matches.length === 1 ? matches[0].id : null
}

/** The subset of an order line needed to compute receiving progress. */
export type ReceivingProgress = Pick<PurchaseOrderItem, 'quantity' | 'received_quantity' | 'remaining_quantity'>

/** Quantity still outstanding on an order line. */
export function remainingQuantity(item: ReceivingProgress): number {
  const remaining = Number(item.remaining_quantity)
  if (Number.isFinite(remaining)) return Math.max(0, remaining)

  const received = Number(item.received_quantity)
  if (Number.isFinite(received)) return Math.max(0, Number(item.quantity || 0) - received)

  return Math.max(0, Number(item.quantity || 0))
}

/** Quantity already booked in for an order line. */
export function receivedQuantity(item: ReceivingProgress): number {
  const received = Number(item.received_quantity)
  if (Number.isFinite(received)) return Math.max(0, received)

  return Math.max(0, Number(item.quantity || 0) - remainingQuantity(item))
}

/** Total still outstanding across every line of an order. */
export function outstandingQuantity(items: readonly ReceivingProgress[]): number {
  const total = items.reduce((sum, item) => sum + remainingQuantity(item), 0)
  return Number(total.toFixed(4))
}

/** Agent lookup option (supplier / warehouse). */
export interface AgentOption {
  id: number
  name: string
  phone?: string | null
  /**
   * Role names. `GET /agents` returns them as an array
   * (`AgentRepository::mapAgentRow` explodes the SQL `GROUP_CONCAT`), but a raw
   * comma string is tolerated too.
   */
  roles?: string[] | string | null
}

/** Role names as a clean list, accepting an array or a comma string. */
export function agentRoleList(roles: AgentOption['roles']): string[] {
  if (Array.isArray(roles)) {
    return roles.filter((role): role is string => typeof role === 'string' && role.trim() !== '')
  }

  if (typeof roles === 'string' && roles.trim() !== '') {
    return roles.split(',').map((role) => role.trim()).filter((role) => role !== '')
  }

  return []
}

/** Role names joined for display (for example `customer · supplier`). */
export function agentRoleLabel(roles: AgentOption['roles'], separator = ' · '): string {
  return agentRoleList(roles).join(separator)
}

/** Currency lookup option for the currency selector. */
export interface CurrencyOption {
  id: number
  code: string
  name?: string | null
  symbol?: string | null
  is_active?: number | boolean
}

/** Product lookup option for the order-line picker. */
export interface ProductOption {
  id: number
  name: string
  sku: string | null
  type?: string | null
  unit: string | null
}

/** UOM row from `GET /products/{id}/uoms` → `data.items[]`. */
export interface ProductUom {
  id?: number
  product_id?: number
  uom_code: string
  label: string
  /** Base units per one of this UOM (1 BOX of factor 20 = 20 base units). */
  to_base_factor: number
  is_base: number | boolean
  is_fractional_allowed?: number | boolean
  display_order?: number
  is_active?: number | boolean
}

/** One quantity entry in a line's multi-UOM breakdown. */
export interface QuantityEntry {
  uom_code: string
  quantity: number | string
}

/** The product's base UOM, when one is configured. */
export function baseProductUom(uoms: readonly ProductUom[]): ProductUom | null {
  return uoms.find((uom) => Boolean(uom.is_base)) ?? null
}

/**
 * Name of the product's main UOM: the product-level `unit` when set, otherwise
 * the configured base UOM's label/code. The UOM `items[]` are the *additional*
 * units, so the main unit always comes from `unit`.
 */
export function baseUomName(uoms: readonly ProductUom[], fallbackUnit?: string | null): string {
  const unit = fallbackUnit?.trim()
  if (unit) return unit

  const base = baseProductUom(uoms)
  return base ? (base.label || base.uom_code) : ''
}

/**
 * Convert a multi-UOM breakdown into the base-unit quantity, the value the
 * purchase-order API stores. Unknown UOM codes are treated as the base unit.
 */
export function baseQuantity(entries: readonly QuantityEntry[], uoms: readonly ProductUom[]): number {
  const factorByCode = new Map(uoms.map((uom) => [uom.uom_code, Number(uom.to_base_factor) || 1]))

  const total = entries.reduce((sum, entry) => {
    const factor = factorByCode.get(entry.uom_code) ?? 1
    return sum + Number(entry.quantity || 0) * factor
  }, 0)

  return Number(total.toFixed(4))
}

/** Normalized paginated result shared by the option-list endpoints. */
export interface PagedResult<T> {
  items: T[]
  meta: { page: number; per_page: number; total: number; total_pages: number }
}

/** Statuses that still need goods to arrive. */
export const OPEN_PURCHASE_ORDER_STATUSES: Array<PurchaseOrder['status']> = [
  'approved',
  'ordered',
  'partially_received',
]

export function isOpenPurchaseOrder(status: string): boolean {
  return OPEN_PURCHASE_ORDER_STATUSES.includes(status)
}

/** Badge class for one purchase-order status. */
export function purchaseOrderStatusClass(status: string): string {
  switch (status) {
    case 'approved':
      return 'status-approved'
    case 'ordered':
      return 'status-ordered'
    case 'partially_received':
      return 'status-partial'
    case 'received':
      return 'status-received'
    case 'cancelled':
      return 'status-cancelled'
    case 'draft':
    default:
      return 'status-draft'
  }
}

/** Whole days from `today` until an ISO date (negative when the date is past). */
export function daysUntil(date: string | null, today: Date): number | null {
  if (!date) return null

  const target = new Date(`${date.slice(0, 10)}T00:00:00`)
  if (Number.isNaN(target.getTime())) return null

  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  return Math.round((target.getTime() - start.getTime()) / 86_400_000)
}

/**
 * Delivery urgency for an open order: `overdue` when the expected date has
 * passed, `due` when it is within the next `dueDays` days, otherwise `null`.
 */
export function deliveryUrgency(
  order: Pick<PurchaseOrder, 'status' | 'expected_date'>,
  today: Date,
  dueDays = 3,
): 'overdue' | 'due' | null {
  if (!isOpenPurchaseOrder(order.status)) return null

  const days = daysUntil(order.expected_date, today)
  if (days === null) return null
  if (days < 0) return 'overdue'
  if (days <= dueDays) return 'due'

  return null
}

/** Sum of the received quantity per product, from captured receipts. */
export function receivedByProduct(
  items: readonly PurchaseOrderItem[],
  received: Record<number, number> = {},
): Map<number, number> {
  const totals = new Map<number, number>()

  for (const item of items) {
    totals.set(item.product_id, Number(received[item.product_id] ?? 0))
  }

  return totals
}

/* ------------------------------------------------------------------ *
 * Supplier invoices (`App\Modules\SupplierInvoices`)
 * Permissions: `supplier_invoices.view | create | update | delete`.
 * ------------------------------------------------------------------ */

export type SupplierInvoiceStatus = 'open' | 'partial' | 'closed' | 'void'

export const SUPPLIER_INVOICE_STATUSES: SupplierInvoiceStatus[] = ['open', 'partial', 'closed', 'void']

/** Row returned by `GET /supplier-invoices` and `GET /supplier-invoices/{id}`. */
export interface SupplierInvoice {
  id: number
  supplier_agent_id: number
  supplier_name: string | null
  purchase_order_id: number | null
  invoice_number: string
  invoice_date: string
  due_date: string
  currency_id: number | null
  currency_code: string | null
  exchange_rate: number
  invoice_amount: number
  /** Settlement status is allocation-derived; only `void` is set by hand. */
  status: SupplierInvoiceStatus | string
  department_agent_id: number | null
  department_name: string | null
  warehouse_agent_id: number | null
  warehouse_name: string | null
  notes: string | null
  created_at: string | null
  updated_at: string | null
}

/** Filters accepted by `GET /supplier-invoices`. */
export interface SupplierInvoiceFilters {
  supplier_agent_id?: number
  status?: string
  currency_code?: string
}

/** Body for `POST /supplier-invoices` and `PUT /supplier-invoices/{id}`. */
export interface SaveSupplierInvoicePayload {
  supplier_agent_id: number
  purchase_order_id?: number | null
  invoice_number: string
  invoice_date: string
  due_date: string
  currency_code: string
  exchange_rate?: number
  invoice_amount: number
  /** Send `void` to void an invoice; any other value is rejected by the API. */
  status?: SupplierInvoiceStatus
  department_agent_id?: number | null
  warehouse_agent_id?: number | null
  notes?: string | null
}

/** Badge class for one supplier-invoice status. */
export function supplierInvoiceStatusClass(status: string): string {
  switch (status) {
    case 'partial':
      return 'status-partial'
    case 'closed':
      return 'status-received'
    case 'void':
      return 'status-cancelled'
    case 'open':
    default:
      return 'status-approved'
  }
}

/** Statuses that still carry an unsettled balance. */
export const OPEN_SUPPLIER_INVOICE_STATUSES: string[] = ['open', 'partial']

export function isOpenSupplierInvoice(status: string): boolean {
  return OPEN_SUPPLIER_INVOICE_STATUSES.includes(status)
}

/** An open invoice whose due date has already passed. */
export function isOverdueInvoice(
  invoice: Pick<SupplierInvoice, 'status' | 'due_date'>,
  today: Date,
): boolean {
  if (!isOpenSupplierInvoice(invoice.status)) return false

  const days = daysUntil(invoice.due_date, today)
  return days !== null && days < 0
}

/** Whole days left before the invoice due date (negative when overdue). */
export function invoiceDueInDays(
  invoice: Pick<SupplierInvoice, 'due_date'>,
  today: Date,
): number | null {
  return daysUntil(invoice.due_date, today)
}
